import { useCallback, useEffect, useState } from 'react';
import {
  enrichStoresWithDistance,
  filterStoresByRadius,
  sortStoresByDistance,
} from '../utils/distanceUtils';
import { getCurrentLocation } from '../services/geolocation.service';

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };

export const useStoreMap = (stores) => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(11);
  const [selectedStore, setSelectedStore] = useState(null);
  const [searchRadius, setSearchRadius] = useState(50); // km
  const [showSearchRadius, setShowSearchRadius] = useState(false);

  // Get user location
  const findUserLocation = useCallback(() => {
    setLocationLoading(true);
    setLocationError(null);

    getCurrentLocation(
      (location) => {
        setUserLocation(location);
        setMapCenter({ lat: location.lat, lng: location.lng });
        setMapZoom(13);
        setLocationLoading(false);
      },
      (error) => {
        setLocationError(error);
        setLocationLoading(false);
      }
    );
  }, []);

  // Get stores near user
  const getNearbyStores = useCallback(() => {
    if (!userLocation || !stores.length) return [];

    const storesWithCoordinates = stores.filter(
      (store) => Number.isFinite(store.lat) && Number.isFinite(store.lng)
    );
    const enriched = enrichStoresWithDistance(
      storesWithCoordinates,
      userLocation.lat,
      userLocation.lng
    );
    const nearby = filterStoresByRadius(enriched, userLocation.lat, userLocation.lng, searchRadius);
    return sortStoresByDistance(nearby, userLocation.lat, userLocation.lng);
  }, [stores, userLocation, searchRadius]);

  // Get stores with distance info
  const storesWithDistance = useCallback(() => {
    const storesWithCoordinates = stores.filter(
      (store) => Number.isFinite(store.lat) && Number.isFinite(store.lng)
    );

    if (!userLocation) return storesWithCoordinates;
    return enrichStoresWithDistance(storesWithCoordinates, userLocation.lat, userLocation.lng);
  }, [stores, userLocation]);

  // Handle store click on map
  const handleStoreMarkerClick = useCallback((store) => {
    setSelectedStore(store);

    if (Number.isFinite(store?.lat) && Number.isFinite(store?.lng)) {
      setMapCenter({ lat: store.lat, lng: store.lng });
      setMapZoom(16);
    }
  }, []);

  // Open store info window
  const openStoreInfo = useCallback((store) => {
    handleStoreMarkerClick(store);
  }, [handleStoreMarkerClick]);

  useEffect(() => {
    if (!stores.length) {
      return;
    }

    const firstStoreWithCoordinates = stores.find(
      (store) => Number.isFinite(store.lat) && Number.isFinite(store.lng)
    );

    if (firstStoreWithCoordinates && !selectedStore) {
      setSelectedStore(firstStoreWithCoordinates);
      setMapCenter({ lat: firstStoreWithCoordinates.lat, lng: firstStoreWithCoordinates.lng });
      setMapZoom(11);
    }
  }, [stores, selectedStore]);
  return {
    // Location
    userLocation,
    locationLoading,
    locationError,
    findUserLocation,
    
    // Map state
    mapCenter,
    setMapCenter,
    mapZoom,
    setMapZoom,
    selectedStore,
    setSelectedStore,
    handleStoreMarkerClick,
    openStoreInfo,
    
    // Search radius
    searchRadius,
    setSearchRadius,
    showSearchRadius,
    setShowSearchRadius,
    
    // Store data
    storesWithDistance: storesWithDistance(),
    nearbyStores: getNearbyStores(),
    getNearbyStores,
  };
};
